import { useEffect, useMemo } from 'react';
import { GoMotoFunction } from '../../API/GoMoto.types';
import { UseSuppressAutofillStyleHook } from './useSuppressAuotfillStyle.types';
import css from './useSuppressAutofillStyle.module.scss';

/**
 * @description
 * Suppresses the autofill styling of browsers by using the beginning of the autofill animation
 * to suppress the animation entirely and completely prevent the styling from affecting the field.
 *
 * In order for this to work, you must pass an input ref to this hook for the input itself, and then
 * you must apply the classname returned to the input element itself.
 *
 * @param inputRef - {@link UseSuppressAutofillStyleHook inputRef} description.
 */
export const useSuppressAutofillStyle: UseSuppressAutofillStyleHook = ({
    inputRef,
}) => {
    useEffect(() => {
        const inputElement: HTMLElement | null = inputRef.current;

        const handleAnimationStartEvent: GoMotoFunction<[AnimationEvent], void> = (event: AnimationEvent) => {
            if (inputRef.current) {
                switch (event.animationName) {
                    case css.onAutoFillStart:
                        inputRef.current.setAttribute('autoFill', 'true');
                        break;
                    case css.onAutoFillCancel:
                        inputRef.current.setAttribute('autoFill', 'false');
                        break;
                    default:
                        break;
                }
            }
        };

        if (inputElement) {
            /**
             * Listen to when the animation starts for an autofill and adjust the autofill attribute.
             * This prevent autofill styling from being applied so that the input listener suppressing strings with spaces doesn't break the field.
             */
            inputElement.addEventListener('animationstart', handleAnimationStartEvent);
        }

        return () => {
            if (inputElement) {
                inputElement.removeEventListener('animationstart', handleAnimationStartEvent);
            }
        };
    }, [inputRef]);

    return useMemo(() => {
        return {
            inputClassName: css.suppressAutofillStyle,
        };
    }, []);
};